import React, { useState } from 'react';
import {
  Box,
  ThemeProvider,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, EmailOutlined, LockOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { Navigate, useNavigate } from 'react-router-dom';
import { loginWithAuth0, loginWithGoogle } from '../../services/apiService';
import { theme } from '../../style/theme';
import { motion } from 'framer-motion';
import datavLogo from '../../Images/datav_logo.png';
import { PrimaryFooter } from '../../components/ui/footer';

export function LoginPage() {
  const navigate = useNavigate();
  const authToken = Cookies.get('authToken');
  const requiresPasswordChange =
    Cookies.get('requiresPasswordChange') === 'true';

  if (authToken) {
    if (requiresPasswordChange) {
      return <Navigate to="/change-password" replace />;
    }
    return <Navigate to="/userdashboard" replace />;
  }

  const [formData, setFormData] = useState({ username: '', password: '' });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const handlePasswordVisibility = () => setShowPassword((prev) => !prev);

  const validateEmail = (email) => {
    if (!email) return 'Email is required';
    return '';
  };

  const validatePassword = (password) => {
    if (!password) return 'Password is required';
    return '';
  };

  const validateLogin = () => {
    const newErrors = {};
    const emailError = validateEmail(formData.username);
    const passwordError = validatePassword(formData.password);
    if (emailError) newErrors.username = emailError;
    if (passwordError) newErrors.password = passwordError;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = '';
    if (name === 'username') {
      error = validateEmail(value);
    } else if (name === 'password') {
      error = validatePassword(value);
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    let error = '';
    if (name === 'username') {
      error = validateEmail(value.trim());
    } else if (name === 'password') {
      error = validatePassword(value);
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    if (!validateLogin()) return;
    try {
      const { token, user, backendUser } = await loginWithAuth0(
        formData.username,
        formData.password,
      );
      Cookies.set('authToken', token, { expires: 1 });
      Cookies.set('userId', backendUser.user_id, { expires: 1, secure: true });
      Cookies.set('Org_id', backendUser.primary_org_id, {
        expires: 1,
        secure: true,
      });
      Cookies.set('Role_id', backendUser.role_id, { expires: 1, secure: true });
      Cookies.set(
        'username',
        user.username || user.name || user.nickname || user.email || 'Guest',
        { expires: 1, secure: true },
      );

      const needsPasswordChange =
        backendUser?.requires_password_change ||
        backendUser?.must_change_password;
      Cookies.set(
        'requiresPasswordChange',
        needsPasswordChange ? 'true' : 'false',
        { expires: 1, secure: true },
      );

      toast.success('Login successful!', { autoClose: 2000 });

      if (needsPasswordChange) {
        setTimeout(() => navigate('/change-password'), 2000);
      } else {
        setTimeout(() => navigate('/userdashboard'), 2000);
      }
    } catch (error) {
      const errorResponse = error.response?.data;
      const errorDetail = errorResponse?.detail;
      
      const actualError = errorDetail || errorResponse;
      
      const errorCode = actualError?.error || errorResponse?.error || errorResponse?.code;
      const errorMessage = 
        actualError?.message || 
        errorResponse?.message || 
        errorResponse?.error_description || 
        errorResponse?.description || 
        errorDetail?.message ||
        error.message ||
        '';
      const statusCode = error.response?.status;
      
      console.log('[LoginPage] Error response:', {
        errorResponse,
        errorDetail,
        actualError,
        errorCode,
        errorMessage,
        statusCode
      });
      
      const isAccountInactive = 
        errorCode === 'Account is inactive' ||
        actualError?.account_deactivated === true ||
        errorResponse?.account_deactivated === true ||
        (statusCode === 403 && errorMessage?.toLowerCase().includes('account has been deactivated'));
      
      const isTrialExpired = 
        actualError?.trial_expired === true ||
        errorResponse?.trial_expired === true ||
        errorCode === 'Trial period expired' ||
        errorMessage?.toLowerCase().includes('trial period has expired') ||
        errorMessage?.toLowerCase().includes('trial expired') ||
        (statusCode === 403 && errorMessage?.toLowerCase().includes('trial'));
      
      const message = (errorMessage && errorMessage.trim()) || errorCode || 'Unknown error';
      
      if ((isAccountInactive || isTrialExpired) && statusCode === 403) {
        toast.error(message, {
          autoClose: 5000,
          style: {
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            color: 'white',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
          },
        });
        setTimeout(() => navigate('/contact'), 2000);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    }
  };

  const handleGoogleLogin = async () => {
    try {
      const { token, user, backendUser } = await loginWithGoogle();
      Cookies.set('authToken', token, { expires: 1, secure: true });
      Cookies.set('userId', backendUser.user_id, { expires: 1, secure: true });
      Cookies.set('Org_id', backendUser.primary_org_id, {
        expires: 1,
        secure: true,
      });
      Cookies.set('Role_id', backendUser.role_id, { expires: 1, secure: true });
      Cookies.set(
        'username',
        user.username || user.name || user.nickname || user.email || 'Guest',
        { expires: 1, secure: true },
      );

      const needsPasswordChange = backendUser?.requires_password_change || backendUser?.must_change_password;
      Cookies.set('requiresPasswordChange', needsPasswordChange ? 'true' : 'false', { expires: 1, secure: true });

      toast.success('Login successful!', { autoClose: 2000 });
      if (needsPasswordChange) {
        setTimeout(() => navigate('/change-password'), 2000);
      } else {
        setTimeout(() => navigate('/userdashboard'), 2000);
      }
    } catch (error) {
      const errorResponse = error.response?.data;
      const errorDetail = errorResponse?.detail;
      
      const actualError = errorDetail || errorResponse;
      
      const errorCode = actualError?.error || errorResponse?.error || errorResponse?.code;
      const errorMessage = 
        actualError?.message || 
        errorResponse?.message || 
        errorResponse?.error_description || 
        errorResponse?.description || 
        errorDetail?.message ||
        error.message ||
        '';
      const statusCode = error.response?.status;
      
      console.log('[LoginPage Google] Error response:', {
        errorResponse,
        errorDetail,
        actualError,
        errorCode,
        errorMessage,
        statusCode
      });
      
      const isAccountInactive = 
        errorCode === 'Account is inactive' ||
        actualError?.account_deactivated === true ||
        errorResponse?.account_deactivated === true ||
        (statusCode === 403 && errorMessage?.toLowerCase().includes('account has been deactivated'));
      
      const isTrialExpired = 
        actualError?.trial_expired === true ||
        errorResponse?.trial_expired === true ||
        errorCode === 'Trial period expired' ||
        errorMessage?.toLowerCase().includes('trial period has expired') ||
        errorMessage?.toLowerCase().includes('trial expired') ||
        (statusCode === 403 && errorMessage?.toLowerCase().includes('trial'));
      
      const message = errorMessage || errorCode || error.message || 'Unknown error';
      
      if ((isAccountInactive || isTrialExpired) && statusCode === 403) {
        toast.error(message, {
          autoClose: 5000,
          style: {
            background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
            color: 'white',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(239, 68, 68, 0.3)',
          },
        });
        setTimeout(() => navigate('/contact'), 2000);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          background: '#e8fafc',
          position: 'relative',
          overflow: 'hidden',
        }}
        >
        <Box
          sx={{
            width: '100%',
            p: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1, sm: 1, md: 1 },
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
        >
          <Box
            component="img"
            src={datavLogo}
            alt="Dataverse"
            onClick={() => navigate('/')}
            sx={{
              cursor: 'pointer',
              height: 'auto',
              maxWidth: { xs: '90px', sm: '115px', md: '135px' },
              width: { xs: '54px', sm: '72px', md: '85px' },
              objectFit: 'contain',
              display: 'block',
              imageRendering: '-webkit-optimize-contrast',
              WebkitImageRendering: 'crisp-edges',
              msImageRendering: 'crisp-edges',
              backfaceVisibility: 'hidden',
              transform: 'translateZ(0)',
              WebkitFontSmoothing: 'antialiased',
              MozOsxFontSmoothing: 'grayscale',
              '&:hover': {
                opacity: 0.8,
              },
              transition: 'opacity 0.2s ease',
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1.5, sm: 2, md: 3 },
            pb: { xs: 3, sm: 4, md: 4 },
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            style={{ width: '100%', maxWidth: '556px' }}
          >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              p: { xs: 2, sm: 2.5, md: 3 },
              background: '#FFFFFF',
              borderRadius: '8px',
              boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
            }}
          >
            <Box sx={{ width: '100%' }}>
              <Typography
                sx={{
                  fontFamily: '"IBM Plex Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.4375rem', sm: '1.4375rem', md: '1.4375rem' },
                  textAlign: 'center',
                  mb: { xs: 0.75, sm: 0.875, md: 0.875 },
                  color: '#383838',
                  lineHeight: '100%',
                }}
              >
                Login
              </Typography>
              
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 400,
                  fontSize: { xs: '0.84375rem', sm: '0.84375rem', md: '0.84375rem' },
                  textAlign: 'center',
                  mb: { xs: 1.75, sm: 2, md: 2 },
                  color: '#4A5565',
                  lineHeight: '1.5rem',
                  letterSpacing: '-0.31px',
                  px: { xs: 1, sm: 0 },
                }}
              >
                Welcome back. Log in to access your data pipelines and integrations.
              </Typography>

              <Box
                component="form"
                onSubmit={handleLogin}
                sx={{ mt: { xs: 0.5, sm: 0.5, md: 0.5 } }}
              >
                <Box sx={{ mb: { xs: 1.25, sm: 1.5, md: 1.25 } }}>
                  <Typography
                    sx={{
                      fontFamily: '"IBM Plex Sans", sans-serif',
                      fontWeight: 500,
                      fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
                      color: '#383838',
                      lineHeight: '100%',
                      mb: { xs: 0.5, sm: 0.625, md: 0.5 },
                    }}
                  >
                    Email
                  </Typography>
                  <TextField
                    fullWidth
                    name="username"
                    placeholder="you@example.com"
                    value={formData.username}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.username}
                    helperText={errors.username}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment 
                          position="start"
                          sx={{
                            minWidth: { xs: '40px', sm: '48px', md: '48px' },
                            mr: { xs: 0.5, sm: 0.5, md: 0.5 },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <EmailOutlined
                            sx={{
                              color: '#9CA3AF',
                              fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                            }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#D1D5DB',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0056D2',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: { xs: 1.25, sm: 1.375, md: 1.25 },
                        px: { xs: 1.75, sm: 2, md: 2 },
                        color: '#0A0A0A',
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                        lineHeight: '100%',
                        letterSpacing: '-0.31px',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#0A0A0A80',
                        opacity: 1,
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                        lineHeight: '100%',
                        letterSpacing: '-0.31px',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#EF4444',
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                        mt: 0.5,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ mb: { xs: 1.5, sm: 2, md: 1.5 } }}>
                  <Typography
                    sx={{
                      fontFamily: '"IBM Plex Sans", sans-serif',
                      fontWeight: 500,
                      fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
                      color: '#383838',
                      lineHeight: '100%',
                      mb: { xs: 0.5, sm: 0.625, md: 0.5 },
                    }}
                  >
                    Password
                  </Typography>
                  <TextField
                    fullWidth
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment 
                          position="start"
                          sx={{
                            minWidth: { xs: '40px', sm: '48px', md: '48px' },
                            mr: { xs: 0.5, sm: 1, md: 1 },
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <LockOutlined
                            sx={{
                              color: '#9CA3AF',
                              fontSize: { xs: '1.0625rem', sm: '1.0625rem', md: '1.0625rem' },
                            }}
                          />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handlePasswordVisibility}
                            edge="end"
                            size="small"
                            sx={{
                              color: '#9CA3AF',
                              p: { xs: 0.5, sm: 1, md: 1 },
                              '&:hover': {
                                color: '#6B7280',
                                backgroundColor: 'transparent',
                              },
                            }}
                          >
                            {showPassword ? (
                              <VisibilityOff
                                sx={{
                                  fontSize: { xs: '1.0625rem', sm: '1.1875rem', md: '1.1875rem' },
                                }}
                              />
                            ) : (
                              <Visibility
                                sx={{
                                  fontSize: { xs: '1.0625rem', sm: '1.1875rem', md: '1.1875rem' },
                                }}
                              />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '4px',
                        fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                        '& fieldset': {
                          borderColor: '#E5E7EB',
                          borderWidth: '1px',
                        },
                        '&:hover fieldset': {
                          borderColor: '#D1D5DB',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#0056D2',
                          borderWidth: '2px',
                        },
                      },
                      '& .MuiInputBase-input': {
                        py: { xs: 1.25, sm: 1.375, md: 1.25 },
                        px: { xs: 1.75, sm: 2, md: 2 },
                        color: '#0A0A0A',
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                        lineHeight: '100%',
                        letterSpacing: '-0.31px',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: '#0A0A0A80',
                        opacity: 1,
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                        lineHeight: '100%',
                        letterSpacing: '-0.31px',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#EF4444',
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: '0.65625rem', sm: '0.71875rem', md: '0.71875rem' },
                        mt: 0.5,
                      },
                    }}
                  />
                </Box>

                <Box sx={{ textAlign: 'right', mb: { xs: 1.5, sm: 1.5, md: 1.5 } }}>
                  <Link
                    onClick={() => navigate('/forgotpwd')}
                    sx={{
                      fontFamily: '"Open Sans", sans-serif',
                      fontSize: { xs: '0.84375rem', sm: '0.78125rem', md: '0.78125rem' },
                      lineHeight: '22px',
                      letterSpacing: '0%',
                      cursor: 'pointer',
                      color: '#0F172A',
                      textDecoration: 'none',
                      fontWeight: 500,
                      display: 'inline-block',
                      verticalAlign: 'middle',
                      '&:hover': {
                        textDecoration: 'underline',
                        color: '#1E293B',
                      },
                      transition: 'color 0.2s ease',
                    }}
                  >
                    Forgot Password?
                  </Link>
                </Box>

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    fontFamily: '"IBM Plex Sans", sans-serif',
                    height: { xs: '44px', sm: '44px', md: '46px' },
                    borderRadius: '4px',
                    fontWeight: 600,
                    fontSize: { xs: '0.90625rem', sm: '0.84375rem', md: '0.84375rem' },
                    mb: { xs: 2, sm: 2, md: 2 },
                    background: '#0F172A',
                    color: '#ffffff',
                    textTransform: 'none',
                    boxShadow: '0px 2px 4px rgba(15, 23, 42, 0.2)',
                    '&:hover': {
                      background: '#1E293B',
                      boxShadow: '0px 4px 8px rgba(15, 23, 42, 0.3)',
                    },
                    '&:active': {
                      background: '#0A0F1A',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  Login
                </Button>

                <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 2, md: 2 } }}>
                  <Typography
                    sx={{
                      fontFamily: '"Open Sans", sans-serif',
                      color: '#585858',
                      fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
                      lineHeight: '22px',
                      fontWeight: 600,
                      letterSpacing: '0%',
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: { xs: 0.25, sm: 0.5, md: 0.5 },
                    }}
                  >
                    Don&apos;t have an account?{' '}
                    <Link
                      onClick={() => navigate('/signup')}
                      sx={{
                        fontFamily: '"Open Sans", sans-serif',
                        cursor: 'pointer',
                        color: '#585858',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: { xs: '0.78125rem', sm: '0.78125rem', md: '0.78125rem' },
                        lineHeight: '22px',
                        letterSpacing: '0%',
                        '&:hover': {
                          textDecoration: 'underline',
                          color: '#585858',
                        },
                        transition: 'color 0.2s ease',
                      }}
                    >
                      Sign Up
                    </Link>
                  </Typography>
                </Box>

                <Divider
                  sx={{
                    mb: { xs: 2, sm: 2, md: 2 },
                    color: '#0F172A',
                    fontSize: { xs: '0.84375rem', sm: '0.84375rem', md: '0.84375rem' },
                    fontFamily: '"IBM Plex Sans", sans-serif',
                    fontWeight: 500,
                    letterSpacing: '0.1px',
                    lineHeight: '100%',
                    '&::before, &::after': {
                      borderColor: '#E5E7EB',
                    },
                  }}
                >
                  or Sign in with
                </Divider>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleGoogleLogin}
                  sx={{
                    fontFamily: '"Inter", sans-serif',
                    height: { xs: '44px', sm: '44px', md: '46px' },
                    borderRadius: { xs: '4px', sm: '4px', md: '8px' },
                    fontSize: { xs: '0.78125rem', sm: '0.84375rem', md: '0.84375rem' },
                    fontWeight: 500,
                    color: '#383838',
                    borderColor: '#E5E7EB',
                    backgroundColor: '#FFFFFF',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#F9FAFB',
                      borderColor: '#D1D5DB',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                >
                  <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 28 28"
                    fill="none"
                    sx={{
                      width: { xs: '18px', sm: '20px', md: '20px' },
                      height: { xs: '18px', sm: '20px', md: '20px' },
                      mr: 1,
                    }}
                  >
                    <path
                      d="M27.5007 14.0819C27.5007 12.9493 27.4088 12.1227 27.2099 11.2656H14.2764V16.3777H21.868C21.715 17.6482 20.8885 19.5614 19.0518 20.847L19.026 21.0182L23.1154 24.1861L23.3987 24.2144C26.0006 21.8114 27.5007 18.2757 27.5007 14.0819Z"
                      fill="#4285F4"
                    />
                    <path
                      d="M14.2764 27.5508C17.9957 27.5508 21.1181 26.3263 23.3987 24.2142L19.0518 20.8468C17.8886 21.658 16.3274 22.2243 14.2764 22.2243C10.6337 22.2243 7.54189 19.8214 6.43976 16.5L6.27821 16.5137L2.02607 19.8045L1.97046 19.9591C4.23571 24.459 8.88871 27.5508 14.2764 27.5508Z"
                      fill="#34A853"
                    />
                    <path
                      d="M6.43936 16.5005C6.14855 15.6434 5.98025 14.725 5.98025 13.776C5.98025 12.827 6.14855 11.9087 6.42406 11.0516L6.41636 10.869L2.11093 7.52539L1.97006 7.59239C1.03644 9.45974 0.500732 11.5567 0.500732 13.776C0.500732 15.9954 1.03644 18.0922 1.97006 19.9596L6.43936 16.5005Z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M14.2764 5.32642C16.8631 5.32642 18.6079 6.44374 19.6029 7.37746L23.4905 3.58158C21.1029 1.36223 17.9957 0 14.2764 0C8.88871 0 4.23571 3.09177 1.97046 7.59167L6.42446 11.0508C7.54189 7.72947 10.6337 5.32642 14.2764 5.32642Z"
                      fill="#EB4335"
                    />
                  </Box>
                  Continue with Google
                </Button>
              </Box>
            </Box>
          </Paper>
          </motion.div>
        </Box>
      </Box>
      <PrimaryFooter />
    </ThemeProvider>
  );
}

export default LoginPage;
